<template>
  <header class="w-full backdrop-blur-sm bg-white/80 border-b border-gray-200/60 sticky top-0 z-20">
    <div class="relative z-10 flex-shrink-0 h-16 flex items-center">
      <button
        type="button"
        class="border-r border-gray-200/60 px-4 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/50 md:hidden transition-colors duration-200"
        @click="openMobileMenu"
      >
        <span class="sr-only">Open sidebar</span>
        <Bars3BottomLeftIcon
          class="h-6 w-6"
          aria-hidden="true"
        />
      </button>
      <div class="flex-1 flex justify-between items-center px-4 sm:px-6">
        <div class="flex-1 flex items-center">
          <div class="bg-gradient-to-r from-primary/10 to-indigo-50 rounded-lg px-4 py-2">
            <p
              class="text-sm leading-5 font-medium text-gray-800 truncate"
              v-text="greetings"
            />
          </div>
        </div>
        <div class="ml-4 flex items-center gap-3 sm:gap-4">
          <UpgradeSubscriptionCTA v-if="canUpgradeCurrentSubscriptionPlan" />
          
          <button
            type="button"
            class="relative p-2 rounded-lg hover:bg-gray-100/80 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-200 group"
            @click="openNotifications"
          >
            <BellIcon class="h-5 w-5 text-gray-600 group-hover:text-primary transition-colors duration-200" />
            <span
              v-if="unreadNotificationsCount > 0"
              class="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full ring-2 ring-white animate-pulse"
              v-text="unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount"
            />
          </button>

          <!-- Profile dropdown -->
          <div
            ref="menuContainer"
            class="relative"
          >
            <button
              id="user-menu-button"
              type="button"
              class="flex items-center p-1 rounded-lg hover:bg-gray-100/80 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-200 group"
              aria-expanded="false"
              aria-haspopup="true"
              @click.prevent="dropdownMenuOpen = !dropdownMenuOpen"
            >
              <div class="relative h-8 w-8 rounded-full overflow-hidden bg-gradient-to-br from-primary/10 to-indigo-100 ring-2 ring-white shadow-md group-hover:shadow-lg transition-shadow duration-200">
                <svg
                  class="h-full w-full text-primary/70"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </button>
            <transition
              v-click-away="clickAway"
              enter-active-class="transition ease-out duration-200"
              enter-from-class="transform opacity-0 scale-95 -translate-y-1"
              enter-to-class="transform opacity-100 scale-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="transform opacity-100 scale-100 translate-y-0"
              leave-to-class="transform opacity-0 scale-95 -translate-y-1"
            >
              <div
                v-show="dropdownMenuOpen"
                class="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-xl ring-1 ring-black/10 border border-gray-100 py-2 focus:outline-none backdrop-blur-sm"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu-button"
                tabindex="-1"
              >
                <div
                  v-for="item in userNavigation"
                  :key="item.name"
                >
                  <Link
                    v-if="item.inertiaLink"
                    :href="item.href"
                    :class="[
                      item.current ? 'bg-primary/5 text-primary border-r-2 border-primary' : 'text-gray-700 hover:bg-gray-50/80', 
                      'block px-4 py-3 text-sm font-medium transition-all duration-200 hover:translate-x-1'
                    ]"
                    :method="item.method"
                    @click="dropdownMenuOpen = false"
                  >
                    {{ item.name }}
                  </Link>
                  <a
                    v-else
                    :href="item.href"
                    class="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50/80 transition-all duration-200 hover:translate-x-1"
                    @click="dropdownMenuOpen = false"
                  >
                    {{ item.name }}
                  </a>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { useMainStore } from '@/stores/main';
import { Link } from '@inertiajs/vue3';
import useAppData from '@/composables/useAppData';
import { useNotificationsStore } from '@/stores/notifications';
import useBackendRouting from '@/composables/useBackendRouting';
import { Bars3BottomLeftIcon, BellIcon } from '@heroicons/vue/24/outline';
import useRolesAndPermissions from '@/composables/useRolesAndPermissions';
import UpgradeSubscriptionCTA from '@/Components/Subscription/UpgradeSubscriptionCTA.vue';

const emit = defineEmits([
  'open-mobile-menu',
  'open-notifications',
]);

const { t } = useI18n();

const dropdownMenuOpen = ref(false);
const menuContainer = ref(null);

const {
  canUpgradeCurrentSubscriptionPlan,
} = useAppData();

const { route } = useBackendRouting();

const { hasAnyPermission } = useRolesAndPermissions();

const store = useMainStore();
const notificationStore = useNotificationsStore();

const unreadNotificationsCount = computed(() => notificationStore.unreadNotificationsCount);

const userNavigation = computed(() => {
  let items = [];

  if (hasAnyPermission(['access bo'])) {
    items.push({
      name: t('messages.back office'),
      current: false,
      href: '/admin',
      method: 'get',
      inertiaLink: false,
    });
  }

  items.push({
    name: t('messages.my_account'),
    current: window.location.href === route('user.dashboard.profile'),
    href: route('user.dashboard.profile'),
    method: 'get',
    inertiaLink: true,
  });

  if (hasAnyPermission(['view billing'])) {
    items.push({
      name: t('Manage subscription'),
      current: false,
      href: route('broker_dashboard.billing.edit'),
      method: 'get',
      inertiaLink: false,
    });
      
    items.push({
      name: t('My invoices'),
      current: window.location.href === route('broker_dashboard.billing.index'),
      href: route('broker_dashboard.billing.index'),
      method: 'get',
      inertiaLink: true,
    });
  }
    
  items.push({
    name: t('Signed documents'),
    current: window.location.href === route('broker_dashboard.signatures.index'),
    href: route('broker_dashboard.signatures.index'),
    method: 'get',
    inertiaLink: true,
  });

  items.push({
    name: t('messages.sign out'),
    current: false,
    href: route('logout'),
    method: 'get',
    inertiaLink: false,
  });

  return items;
});

const greetings = computed(() => t('messages.greetings_user', { name: store.user ? store.user.first_name : null }));

/**
 * Click away from dropdown.
 *
 * @param event
 */
function clickAway(event) {
  if (!menuContainer.value.contains(event.target)) {
    dropdownMenuOpen.value = false;
  }
}

function openMobileMenu() {
  emit('open-mobile-menu', true);
}

function openNotifications() {
  emit('open-notifications', true);
}
</script>

